import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { translate } from './translationService';
import SentMessage from './SentMessage';
import './Contact.css';

interface ContactProps {
  language: 'en' | 'es' | 'ja';
}

const SERVICE_ID = 'service_ndtm9gd';
const TEMPLATE_ID = 'template_rel1zkl';
const PUBLIC_KEY = 'uqQm1pbA_648rORBJ';

const Contact: React.FC<ContactProps> = ({ language }) => {
  const [formData, setFormData] = useState({ name: '', pronouns: '', email: '', description: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    emailjs.init(PUBLIC_KEY);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg(null);

    const clean = {
      name: formData.name.trim(),
      pronouns: formData.pronouns.trim(),
      email: formData.email.trim(),
      description: formData.description.trim(),
    };

    // Validate ourselves since the form uses noValidate.
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean.email);
    if (!clean.name || !clean.email || !clean.description) {
      setErrorMsg(translate('validationRequired', language));
      return;
    }
    if (!emailOk) {
      setErrorMsg(translate('validationEmail', language));
      return;
    }

    setIsSending(true);

    // IMPORTANT: keys must match your EmailJS template variables exactly
    const templateParams = {
      from_name: clean.name,          // {{from_name}}
      pronouns: clean.pronouns,       // {{pronouns}}  (only if defined in template)
      reply_to: clean.email,          // {{reply_to}} or {{user_email}}
      message: clean.description,     // {{message}}
    };

    try {
      const res = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);
      console.log('EmailJS success:', res.status, res.text);
      setFormSubmitted(true);
    } catch (err: any) {
      console.error('EmailJS error:', err?.status, err?.text || err);
      setErrorMsg(translate('sendError', language));
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => navigate('/');

  return (
    <main className="contact-page fade-in">
      <section className="contact">
        {formSubmitted ? (
          <SentMessage />
        ) : (
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <h2>{translate('contactFormTitle', language)}</h2>

            <label>
              {translate('nameLabel', language)}
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                autoComplete="name"
              />
            </label>

            <label>
              {translate('pronounsLabel', language)}
              <input
                type="text"
                name="pronouns"
                value={formData.pronouns}
                onChange={handleInputChange}
                required
              />
            </label>

            <label>
              {translate('emailLabel', language)}
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="email"
              />
            </label>

            <label>
              {translate('descriptionLabel', language)}
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={5}
              />
            </label>

            {errorMsg && <p className="error-text" role="alert">{errorMsg}</p>}

            <div className="button-group">
              <button type="button" onClick={handleCancel} className="cancel-button">
                {translate('cancelButton', language)}
              </button>
              <button type="submit" className="submit-button" disabled={isSending}>
                {isSending ? translate('sendingLabel', language) : translate('submitButton', language)}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
};

export default Contact;
